import { StateCreator } from 'zustand';
import { QuotationStore } from '../quotationStore.types';
import { Quotation, RevisionLogEntry, DEFAULT_TERMS, DEFAULT_COMPANY, DEFAULT_PAYMENT_MILESTONES, isImmutableStatus } from '@/types/quotation';
import { QuotationRevision } from '@/types/revision';
import { quotationService } from '@/services/quotationService';
import { generateId, generateStandardQuoteNumber } from '@/lib/utils';
import { generateRoomDisplayName } from '@/types/roomMaster';
import { supabase } from '@/lib/supabase';
import { settingsService } from '@/services/settingsService';
import { cacheService } from '@/lib/utils/cacheService';
import { useBdmStore } from '../designConsultantStore';
import { normalizeTextValue } from '@/utils/textNormalization';

export interface QuotationManagementSlice {
    fetchQuotations: (forceFetch?: boolean) => Promise<void>;
    fetchQuotationById: (id: string) => Promise<void>;
    fetchRevisions: (quoteId: string) => Promise<void>;
    restoreRevision: (revisionId: string) => Promise<void>;
    saveQuotation: (whatChanged: string, options?: { status?: string; locked?: boolean; source?: 'summary-shell' | 'hydrated-detail' | 'editor-session' }) => Promise<void>;
    manualSaveDraft: () => Promise<void>;
    deleteQuotation: (quoteId: string) => Promise<void>;
    updateQuotation: (updates: Partial<Quotation>) => void;
    updateMetadata: (updates: unknown) => void;
    updateCompanyContact: (updates: unknown) => void;
    reloadQuotationItems: () => Promise<void>;
    updateQuotationWithCompanyData: () => Promise<void>;
    autoSaveDraft: (force?: boolean) => Promise<void>;
    setAutoSaveEnabled: (enabled: boolean) => void;
    setCurrentQuotation: (quotation: Quotation | null) => void;
    cloneQuotation: (quoteId: string) => Promise<Quotation | null>;
    createNewQuotation: (clientName: string, projectName: string, estimateType: unknown, designConsultantId: string, roomSelections: unknown[]) => Promise<Quotation>;
    fetchingIds: Set<string>;
    autoSaveInFlight: boolean;
    lastAutoSavedSnapshot: string | null;
    lastHydratedSnapshot: string | null;
    lastAutoSaveAt: number;
    draftAvailable: boolean;
    lastDraft: Quotation | null;
    draftBackup: Quotation | null;
    draftRestored: boolean;
    restoreDraft: () => void;
    undoRestoreDraft: () => void;
    discardDraft: () => Promise<void>;

    // Approval Workflow
    requestApproval: () => Promise<void>;
    approveQuotation: (note?: string) => Promise<void>;
    rejectQuotation: (note: string) => Promise<void>;

    // Immutable Lifecycle Actions (backed by DB RPCs)
    /** Creates a new draft revision from a finalized/cancelled/superseded quotation. Navigates to the new draft. */
    createRevision: (parentId: string) => Promise<string | null>;
    /** Duplicates a quotation as a fresh independent draft. Navigates to the new draft. */
    duplicateAsDraft: (sourceId: string) => Promise<string | null>;
    /** Cancels a finalized quotation with a mandatory reason. Commercial fields remain frozen. */
    cancelQuotation: (quoteId: string, reason: string) => Promise<void>;
    /** Calls fn_finalize_quotation_revision — atomically finalizes and supersedes the previous version. */
    finalizeQuotation: (quoteId: string) => Promise<void>;
    toggleTemplateStatus: (quoteId: string, isTemplate: boolean, templateName?: string) => Promise<void>;

    // Permissions
    permissions: unknown[];
    currentUserRole: string | null;
    fetchPermissions: (quoteId: string) => Promise<void>;
    updatePermission: (quoteId: string, email: string, role: string) => Promise<void>;
    removePermission: (quoteId: string, userId: string) => Promise<void>;

    // Session state (kept for backwards compat, sessionUnlocked always false now)
    sessionUnlocked: boolean;
    adminUnlockSession: (quoteNumber: string, typedConfirmation: string) => boolean;
    relockSession: () => void;
}

const createQuotationManagementSliceImpl = (set: unknown, get: unknown): unknown => ({
    fetchingIds: new Set<string>(),
    autoSaveInFlight: false,
    lastAutoSavedSnapshot: null,
    lastHydratedSnapshot: null,
    lastAutoSaveAt: 0,
    draftAvailable: false,
    lastDraft: null,
    draftBackup: null,
    draftRestored: false,
    permissions: [],
    currentUserRole: 'viewer', // Default to viewer for safety
    sessionUnlocked: false,

    adminUnlockSession: (quoteNumber: string, typedConfirmation: string): boolean => {
        const { currentQuotation, currentUserRole } = get();
        if (currentUserRole !== 'admin') return false;
        // Strict case-sensitive match for security
        const isMatch = typedConfirmation.trim() === quoteNumber.trim();
        if (isMatch) {
            set({ sessionUnlocked: true });
        }
        return isMatch;
    },

    relockSession: () => {
        set({ sessionUnlocked: false });
    },
    fetchPermissions: async (quoteId) => {
        try {
            const permissions = await quotationService.getPermissions(quoteId);
            set({ permissions });
        } catch (error) {
            console.error('Failed to fetch permissions', error);
        }
    },

    updatePermission: async (quoteId, email, role) => {
        const { currentUserRole } = get();
        if (currentUserRole !== 'admin') {
            throw new Error('Access Denied: Only Admins can manage permissions.');
        }
        try {
            // 1. Resolve email to userId (Requires profiles table)
            const { data: profile, error } = await supabase
                .from('profiles')
                .select('id')
                .eq('email', email)
                .single();

            if (error || !profile) {
                throw new Error('User not found. They must have logged in at least once.');
            }

            // 2. Set permission
            await quotationService.setPermission(quoteId, profile.id, role.toLowerCase() as unknown);

            // 3. Refresh
            await get().fetchPermissions(quoteId);
        } catch (err: unknown) {
            throw new Error(error.message || 'Failed to update permission');
        }
    },

    removePermission: async (quoteId, userId) => {
        const { currentUserRole } = get();
        if (currentUserRole !== 'admin') {
            throw new Error('Access Denied: Only Admins can remove permissions.');
        }
        try {
            await supabase
                .from('quotation_permissions')
                .delete()
                .match({ quote_id: quoteId, user_id: userId });

            await get().fetchPermissions(quoteId);
        } catch (error) {
            console.error('Failed to remove permission', error);
            throw error;
        }
    },

    fetchQuotations: async (forceFetch = true) => {
        set({ isLoading: true, error: null });
        try {
            // Use a reasonable limit for recent quotations to prevent slow queries
            const quotes = await quotationService.getRecentQuotations(50, !forceFetch);
            const quotesWithUpdatedFinancials = quotes.map(q => {
                return {
                    ...q,
                    hydrationState: 'summary' as const,
                    financials: q.financials || {
                        subtotal: 0,
                        taxName: 'GST',
                        taxRate: 18,
                        taxAmount: 0,
                        discountType: 'percentage',
                        discountValue: 0,
                        discountAmount: 0,
                        grandTotal: 0
                    }
                };
            });
            set({ quotations: quotesWithUpdatedFinancials, isLoading: false });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            set({ error: message, isLoading: false });
        }
    },

    fetchQuotationById: async (id) => {
        const { fetchingIds } = get();

        if (fetchingIds.has(id)) return;

        set((state) => ({
            isLoading: true,
            error: null,
            fetchingIds: new Set(state.fetchingIds).add(id),
        }));

        try {
            console.log(`[Store] Fetching full project detail for: ${id}`);
            const quote = await quotationService.getQuotationById(id);
            if (!quote) {
                // Quote was deleted from DB — clear stale local state
                console.warn(`[Store] Quotation ${id} not found in DB, clearing from store.`);
                set((state) => ({
                    currentQuotation: state.currentQuotation?.metadata?.quoteId === id ? null : state.currentQuotation,
                    isLoading: false,
                    fetchingIds: (() => { const s = new Set(state.fetchingIds); s.delete(id); return s; })()
                }));
                return;
            }

            const quoteWithHydration: Quotation = {
                ...quote,
                hydrationState: 'full' as const
            };

            const isQuoteLocked = quoteWithHydration.metadata.locked || quoteWithHydration.metadata.status === 'finalized';
            const quoteSnapshot = JSON.stringify(quoteWithHydration);

            // 1. Check for drafts (Draft Recovery Logic)
            // DISABLED per user request: The database must be the single source of truth.
            set({ draftAvailable: false, lastDraft: null, draftBackup: null, draftRestored: false });

            // 2. Fetch User Role for RBAC (parallel, safe)
            const { data: { session } } = await supabase.auth.getSession();
            const userId = session?.user?.id;
            let role: string | null = null;

            if (userId) {
                const { data: tenantId } = await supabase.rpc('current_tenant_id');
                const currentTenantId = tenantId as string | null;
                
                const [metaRes, permRes, tenantRoleRes] = await Promise.all([
                    supabase.from('quotations').select('user_id').eq('id', id).maybeSingle(),
                    supabase.from('quotation_permissions').select('role')
                        .eq('quote_id', id).eq('user_id', userId).maybeSingle(),
                    currentTenantId ? supabase.from('tenant_users').select('role')
                        .eq('tenant_id', currentTenantId).eq('user_id', userId).maybeSingle() : Promise.resolve({ data: null })
                ]);

                const tenantRole = tenantRoleRes.data?.role?.toLowerCase();
                if (tenantRole && tenantRole !== 'viewer') {
                    role = tenantRole === 'owner' || tenantRole === 'admin' ? 'admin' : tenantRole;
                } else if (!metaRes.data?.user_id || metaRes.data?.user_id === userId) {
                    role = 'admin'; // Tenant owners/admins and Quote creators/workspace members are always admin
                } else {
                    role = (permRes.data?.role as string)?.toLowerCase() || 'user';
                }
            }

            // Guard: Do NOT overwrite an in-memory finalized/locked quote with a stale DB copy
            // This prevents the race condition where fetchQuotationById finishes AFTER
            // the user finalizes, resetting status back to what was in DB pre-save.
            const existingQuotation = get().currentQuotation;
            const existingIsFinalized = existingQuotation?.metadata?.quoteId === id &&
                (existingQuotation?.metadata?.locked || existingQuotation?.metadata?.status === 'finalized');
            const incomingIsFinalized = quoteWithHydration.metadata.locked || quoteWithHydration.metadata.status === 'finalized';
            const incomingHasHigherVersion = (quoteWithHydration.metadata.version || 0) >= (existingQuotation?.metadata?.version || 0);

            if (existingIsFinalized && !incomingIsFinalized && !incomingHasHigherVersion) {
                console.log(`[Store] Skipping stale fetchQuotationById overwrite — in-memory quote is finalized and newer than DB copy.`);
                set((state) => {
                    const next = new Set(state.fetchingIds);
                    next.delete(id);
                    return { fetchingIds: next, isLoading: false };
                });
                return;
            }

            set({
                currentQuotation: quoteWithHydration,
                currentUserRole: role,
                isLoading: false,
                lastHydratedSnapshot: quoteSnapshot,
                lastAutoSavedSnapshot: quoteSnapshot,
                draftAvailable: !isQuoteLocked && get().draftAvailable,
                lastDraft: !isQuoteLocked ? get().lastDraft : null,
                draftBackup: !isQuoteLocked ? get().draftBackup : null,
                draftRestored: !isQuoteLocked && get().draftRestored,
            });

            setTimeout(() => {
                get().recalculateFinancials();
            }, 0);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            set({ error: message, isLoading: false });
        } finally {
            set((state) => {
                const next = new Set(state.fetchingIds);
                next.delete(id);
                return { fetchingIds: next };
            });
        }
    },

    restoreDraft: () => {
        const { lastDraft, currentQuotation } = get();
        if (lastDraft) {
            set({
                currentQuotation: lastDraft,
                draftAvailable: false,
                draftBackup: currentQuotation,
                draftRestored: true,
            });
            setTimeout(() => get().recalculateFinancials(), 0);
        }
    },

    undoRestoreDraft: () => {
        const { draftBackup, lastDraft } = get();
        if (!draftBackup) return;

        set({
            currentQuotation: draftBackup,
            draftBackup: null,
            draftRestored: false,
            draftAvailable: !!lastDraft,
        });
        setTimeout(() => get().recalculateFinancials(), 0);
    },

    discardDraft: async () => {
        const { currentQuotation } = get();
        if (currentQuotation) {
            await quotationService.deleteDraft(currentQuotation.metadata.quoteId);
            set({ draftAvailable: false, lastDraft: null, draftBackup: null, draftRestored: false });
        }
    },

    fetchRevisions: async (quoteId) => {
        set({ isLoading: true, error: null });
        try {
            const { data, error } = await supabase
                .from('quotation_revisions')
                .select('*')
                .eq('quote_id', quoteId)
                // DB schema uses `version` (not `revision_number`)
                .order('version', { ascending: false });

            if (error) throw error;

            const revisions: QuotationRevision[] = (data || []).map(rev => ({
                id: rev.id,
                quote_id: rev.quote_id,
                revision_number: rev.version,
                quotation_snapshot: rev.snapshot as unknown,
                line_items_snapshot: [] as unknown, // This field doesn't exist in new schema
                changed_at: rev.created_at,
                changed_by: rev.created_by || 'Unknown',
                what_changed: rev.change_summary || 'Updated quotation'
            }));


            set({ revisions, isLoading: false });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            set({ error: message, isLoading: false });
        }
    },

    restoreRevision: async (revisionId) => {
        set({ isLoading: true, error: null });
        try {
            const { data, error } = await supabase
                .from('quotation_revisions')
                .select('*')
                .eq('id', revisionId)
                .single();

            if (error) throw error;
            if (!data) throw new Error('Revision not found');

            const snapshot = data.snapshot as unknown;

            // Restore the quotation from the snapshot
            const snapshotJson = JSON.stringify(snapshot);
            set({
                currentQuotation: snapshot,
                lastHydratedSnapshot: snapshotJson,
                lastAutoSavedSnapshot: snapshotJson,
                isLoading: false
            });

            setTimeout(() => {
                get().recalculateFinancials();
            }, 0);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            set({ error: message, isLoading: false });
        }
    },

    saveQuotation: async (whatChanged, options) => {
        // Cancel any pending debounced auto-save timer to prevent race conditions during publishing/finalization
        if ((globalThis as any)._autoSaveTimeout) {
            clearTimeout((globalThis as any)._autoSaveTimeout);
            (globalThis as any)._autoSaveTimeout = null;
        }

        set({ isLoading: true, error: null });
        const { currentQuotation, quotations, revisions, currentUserRole } = get();
        if (!currentQuotation) {
            set({ isLoading: false, error: 'No quotation loaded' });
            return;
        }

        // Safety Guard Block: Reject save if hydrationState is not 'full'
        if (currentQuotation.hydrationState !== 'full') {
            const errorMsg = `Safety Guard Block: Refusing to save quotation in '${currentQuotation.hydrationState || 'unknown'}' hydration state. Action aborted to prevent data loss.`;
            console.error(errorMsg, { currentQuotation });
            set({ isLoading: false, error: errorMsg });
            throw new Error(errorMsg);
        }

        if (process.env.NODE_ENV !== 'production') {
            const saveSource = options?.source || 'editor-session';
            console.log(`[Dev Save Provenance] Source: ${saveSource}, ID: ${currentQuotation.metadata.quoteId}, Version: ${currentQuotation.metadata.version}`);
        }

        // RBAC: Any authenticated user who is not explicitly a read-only viewer can save/publish
        const roleNormalized = currentUserRole?.toLowerCase();
        const isDisallowedViewer = roleNormalized === 'viewer';
        let canPublish = !isDisallowedViewer;
        
        if (roleNormalized === 'viewer') {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user?.id === currentQuotation.metadata.createdBy ||
                session?.user?.id === (currentQuotation as unknown).user_id) {
                canPublish = true;
            }
        }

        if (!canPublish) {
            const errorMsg = 'Access Denied: You do not have permission to publish (finalize) this quotation.';
            console.error(errorMsg, { currentUserRole });
            set({ isLoading: false, error: errorMsg });
            throw new Error(errorMsg);
        }

        try {
            get().recalculateFinancials();
            const updatedCurrentQuotation = get().currentQuotation!;

            const { data: { user } } = await supabase.auth.getUser();
            const userIdentifier = user ? `${user.user_metadata?.full_name || user.email} (${user.email})` : 'System';

            const nextVersion = updatedCurrentQuotation.metadata.version + 1;

            const revisionEntry: RevisionLogEntry = {
                revisionNumber: nextVersion,
                date: new Date().toISOString(),
                changedBy: userIdentifier,
                whatChanged,
            };

            const currentStatus = updatedCurrentQuotation.metadata.status;
            const currentLocked = updatedCurrentQuotation.metadata.locked || currentStatus === 'finalized';

            const status = options?.status || (currentLocked ? 'finalized' : 'revised');
            const locked = options?.locked ?? (status === 'finalized' || currentLocked);

            const updatedMetadata = {
                ...updatedCurrentQuotation.metadata,
                version: nextVersion,
                status: status as unknown,
                locked: locked,
                quoteNumber: updatedCurrentQuotation.metadata.quoteNumber.includes('-v')
                    ? updatedCurrentQuotation.metadata.quoteNumber.replace(/-v\d+$/i, `-v${nextVersion}`)
                    : `${updatedCurrentQuotation.metadata.quoteNumber}-v${nextVersion}`,
                fileNumber: updatedCurrentQuotation.metadata.fileNumber.includes('-v')
                    ? updatedCurrentQuotation.metadata.fileNumber.replace(/-v\d+$/i, `-v${nextVersion}`)
                    : `${updatedCurrentQuotation.metadata.fileNumber}-v${nextVersion}`,
            };

            const updated: Quotation = {
                ...updatedCurrentQuotation,
                metadata: updatedMetadata,
                hydrationState: 'full',
                revisionHistory: [...updatedCurrentQuotation.revisionHistory, revisionEntry],
                system: {
                    ...updatedCurrentQuotation.system,
                    updatedAt: new Date().toISOString(),
                },
            };

            const revisionSnapshot: QuotationRevision = {
                id: generateId(),
                quote_id: updatedCurrentQuotation.metadata.quoteId,
                revision_number: nextVersion,
                quotation_snapshot: JSON.parse(JSON.stringify(updated)),
                line_items_snapshot: updated.sections.reduce((acc: LineItem[], s: unknown) => {
                    s.roomGroups.forEach((g: unknown) => acc.push(...g.items));
                    return acc;
                }, []),
                changed_at: new Date().toISOString(),
                changed_by: userIdentifier,
                what_changed: whatChanged
            };

            const updatedQuotations = quotations.map((q) =>
                q.metadata.quoteId === updated.metadata.quoteId ? updated : q
            );

            await quotationService.saveQuotation(updated, revisionSnapshot);

            // Delete draft after successful publish (Intent: Publish creates Version + Deletes Draft)
            await quotationService.deleteDraft(updated.metadata.quoteId);

            // Bust list cache so next fetch returns fresh data
            cacheService.invalidateQuotations();

            set({
                currentQuotation: updated,
                quotations: updatedQuotations,
                revisions: [...revisions, revisionSnapshot],
                isLoading: false,
                error: null,
                lastHydratedSnapshot: JSON.stringify(updated),
                lastAutoSavedSnapshot: JSON.stringify(updated),
                lastAutoSaveAt: Date.now(),
                draftAvailable: false,
                lastDraft: null,
                draftBackup: null,
                draftRestored: false,
            });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            console.error('Failed to save quotation:', err);
            set({ error: message, isLoading: false });
        }
    },

    manualSaveDraft: async () => {
        const { currentQuotation, currentUserRole } = get();
        if (!currentQuotation) return;

        if (currentUserRole === 'viewer') {
            set({ error: 'Viewers cannot save drafts' });
            return;
        }

        set({ isLoading: true });
        try {
            await quotationService.saveDraft(currentQuotation);
            const snapshot = JSON.stringify(currentQuotation);
            set({
                isLoading: false,
                lastAutoSavedSnapshot: snapshot,
                lastAutoSaveAt: Date.now(),
            });
        } catch (err) {
            set({ isLoading: false, error: 'Failed to manual save draft' });
        }
    },

    requestApproval: async () => {
        const { currentQuotation, currentUserRole } = get();
        if (!currentQuotation || !currentUserRole) return;

        if (currentUserRole !== 'editor') {
            set({ error: 'Only editors can request approval' });
            return;
        }

        try {
            set({ isLoading: true });
            const updatedMetadata = { ...currentQuotation.metadata, status: 'pending_approval' as const };
            const updatedQuotation = { ...currentQuotation, metadata: updatedMetadata };

            // We update the status in DB
            await quotationService.updateQuotation(currentQuotation.metadata.quoteId, { status: 'pending_approval' });

            set({
                currentQuotation: updatedQuotation,
                isLoading: false
            });
        } catch (err: unknown) {
            set({ isLoading: false, error: err.message || 'Failed to request approval' });
        }
    },

    approveQuotation: async (note) => {
        const { currentQuotation, currentUserRole, saveQuotation } = get();
        if (!currentQuotation || !currentUserRole) return;

        const canApprove = currentUserRole === 'admin' || currentUserRole === 'user';
        if (!canApprove) {
            set({ error: 'Access Denied: Only Admins or Owners can approve quotations' });
            return;
        }

        try {
            // Approving means publishing a finalized version
            await saveQuotation(note || 'Approved by Admin', { status: 'finalized', locked: true });
        } catch (err: unknown) {
            set({ error: err.message || 'Failed to approve quotation' });
        }
    },

    rejectQuotation: async (note) => {
        const { currentQuotation, currentUserRole } = get();
        if (!currentQuotation || !currentUserRole) return;

        if (currentUserRole !== 'admin') {
            set({ error: 'Access Denied: Only Admins can reject quotations' });
            return;
        }

        try {
            set({ isLoading: true });
            // Set status to rejected
            await quotationService.updateQuotation(currentQuotation.metadata.quoteId, {
                status: 'rejected',
                approval_note: note
            });

            // Refresh local state
            const updated = {
                ...currentQuotation,
                metadata: {
                    ...currentQuotation.metadata,
                    status: 'rejected' as const,
                    notes: note
                }
            };
            set({ currentQuotation: updated, isLoading: false });
        } catch (err: unknown) {
            set({ isLoading: false, error: err.message || 'Failed to reject' });
        }
    },

    deleteQuotation: async (quoteId) => {
        set({ isLoading: true, error: null });
        try {
            const result = await quotationService.deleteQuotation(quoteId);
            if (!result) throw new Error('Failed to delete quotation');

            // Bust list cache
            cacheService.invalidateQuotations();

            set((state) => ({
                quotations: state.quotations.filter(q => q.metadata.quoteId !== quoteId),
                currentQuotation: state.currentQuotation?.metadata.quoteId === quoteId ? null : state.currentQuotation,
                isLoading: false
            }));
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            set({ error: message, isLoading: false });
            throw err;
        }
    },

    updateQuotation: (updates) => {
        const { currentQuotation } = get();
        if (!currentQuotation) return;

        // Block updates on any immutable status — no bypass mechanism exists anymore
        if (isImmutableStatus(currentQuotation.metadata.status)) {
            console.warn('[Store] Blocked update: quotation is in immutable status:', currentQuotation.metadata.status);
            return;
        }

        const normalizedUpdates = normalizeTextValue(updates);

        const next: Quotation = {
            ...currentQuotation,
            ...(normalizedUpdates.client ? { client: { ...currentQuotation.client, ...normalizedUpdates.client } } : {}),
            ...(normalizedUpdates.company ? { company: { ...currentQuotation.company, ...normalizedUpdates.company } } : {}),
            ...(normalizedUpdates.financials ? { financials: { ...currentQuotation.financials, ...normalizedUpdates.financials } } : {}),
            ...(normalizedUpdates.terms ? { terms: { ...currentQuotation.terms, ...normalizedUpdates.terms } } : {}),
            ...(normalizedUpdates.rooms ? { rooms: normalizedUpdates.rooms } : {}),
            ...(normalizedUpdates.sections ? { sections: normalizedUpdates.sections } : {}),
            ...(normalizedUpdates.paymentSchedule ? { paymentSchedule: normalizedUpdates.paymentSchedule } : {}),
            ...(normalizedUpdates.proposalNarrative !== undefined ? { proposalNarrative: normalizedUpdates.proposalNarrative } : {}),
        };
        set({ currentQuotation: next });
    },

    updateMetadata: (updates) => {
        const { currentQuotation } = get();
        if (!currentQuotation) return;

        // Block metadata updates on any immutable status
        // Exception: status field itself can change via lifecycle RPCs (handled server-side)
        if (isImmutableStatus(currentQuotation.metadata.status)) {
            console.warn('[Store] Blocked metadata update: quotation is in immutable status:', currentQuotation.metadata.status);
            return;
        }

        set({
            currentQuotation: {
                ...currentQuotation,
                metadata: { ...currentQuotation.metadata, ...updates }
            }
        });
    },

    updateCompanyContact: (updates) => {
        const { currentQuotation } = get();
        if (!currentQuotation) return;

        if (isImmutableStatus(currentQuotation.metadata.status)) return;

        set({
            currentQuotation: {
                ...currentQuotation,
                company: {
                    ...currentQuotation.company,
                    contact: { ...currentQuotation.company.contact, ...updates }
                }
            }
        });
    },

    reloadQuotationItems: async () => {
        set({ isLoading: true, error: null });
        try {
            const idToLoad = get().currentQuotation?.metadata?.quoteId;
            if (!idToLoad) {
                set({ isLoading: false });
                return;
            }

            const quote = await quotationService.getQuotationById(idToLoad);
            if (quote) {
                const snapshot = JSON.stringify(quote);
                set({
                    currentQuotation: quote,
                    lastHydratedSnapshot: snapshot,
                    lastAutoSavedSnapshot: snapshot,
                    isLoading: false
                });
            }
        } catch (err: unknown) {
            set({ error: err instanceof Error ? err.message : 'Failed to reload items', isLoading: false });
        }
    },

    /**
     * Update quotation with fresh company data from settings
     */
    updateQuotationWithCompanyData: async () => {
        const { currentQuotation } = get();
        if (!currentQuotation) return;

        const isNew = currentQuotation.metadata.version === 0 && currentQuotation.metadata.status === 'draft';

        // CRITICAL: Reject update if quotation is in summary hydration state or sections are missing.
        // This prevents triggering safety guard block or wiping out data while fetchQuotationById runs.
        if (currentQuotation.hydrationState !== 'full' && !isNew) {
            console.log('[Store] Skipping company update: quotation is not in full hydration state.');
            return;
        }

        const sections = currentQuotation.sections || [];
        if (sections.length === 0 && !isNew) {
            console.log('[Store] Skipping company update: sections not yet loaded to prevent accidental wipe.');
            return;
        }

        try {
            const companyProfile = settingsService.getCompanyProfile();
            const updatedQuotation = {
                ...currentQuotation,
                company: {
                    ...DEFAULT_COMPANY,
                    legalName: companyProfile.name || DEFAULT_COMPANY.legalName,
                    brandName: companyProfile.name || DEFAULT_COMPANY.brandName,
                    gstin: companyProfile.gst_number || '',
                    address: companyProfile.address ? {
                        line1: companyProfile.address,
                        city: '',
                        state: '',
                        country: '',
                        pincode: '',
                    } : {
                        ...DEFAULT_COMPANY.address,
                        line1: companyProfile.address || '',
                    },
                    contact: {
                        ...DEFAULT_COMPANY.contact,
                        bdmName: currentQuotation.company?.contact?.bdmName || DEFAULT_COMPANY.contact?.bdmName,
                        phone: companyProfile.phone || '',
                        email: companyProfile.email || '',
                    },
                    logoUrl: companyProfile.logo_url || '',
                }
            };

            set({ currentQuotation: updatedQuotation });
            await quotationService.saveDraft(updatedQuotation);
        } catch (err: unknown) {
            set({ error: err instanceof Error ? err.message : 'Failed to update company data' });
        }
    },

    setAutoSaveEnabled: (enabled) => {
        set({ autoSaveEnabled: enabled });
    },

    autoSaveDraft: async (force = false) => {
        const {
            currentQuotation,
            autoSaveEnabled,
            fetchingIds,
            autoSaveInFlight,
            lastAutoSavedSnapshot,
            lastHydratedSnapshot,
            localDrafts,
            setSyncStatus
        } = get();

        if (!currentQuotation) return;

        // Never auto-save an unhydrated quotation ('summary' state) or an immutable/locked quotation
        if (currentQuotation.hydrationState !== 'full') return;
        if (isImmutableStatus(currentQuotation.metadata.status) || currentQuotation.metadata.locked) return;

        if ((!autoSaveEnabled && !force)) return;
        if (fetchingIds.has(currentQuotation.metadata.quoteId) && !force) return;

        const sections = currentQuotation.sections || [];
        const isNewQuote = currentQuotation.metadata.version === 0 && currentQuotation.metadata.status === 'draft';
        if (sections.length === 0 && !isNewQuote && !force) return;

        const snapshot = JSON.stringify(currentQuotation);
        if (!force && (snapshot === lastHydratedSnapshot || snapshot === lastAutoSavedSnapshot)) return;

        // Debounce logic
        if (!force) {
            setSyncStatus('saving');
            if ((globalThis as any)._autoSaveTimeout) {
                clearTimeout((globalThis as any)._autoSaveTimeout);
            }
            (globalThis as any)._autoSaveTimeout = setTimeout(() => {
                get().autoSaveDraft(true);
            }, 3000);
            return;
        }

        if (autoSaveInFlight) return;

        try {
            set({ autoSaveInFlight: true });
            setSyncStatus('saving');
            
            const now = Date.now();
            
            // Clean up old drafts (7-day expiry)
            const expiryTime = now - (7 * 24 * 60 * 60 * 1000);
            const activeDrafts = Object.entries(localDrafts).reduce((acc, [id, draft]) => {
                if (draft.updatedAt > expiryTime) {
                    acc[id] = draft;
                }
                return acc;
            }, {} as Record<string, import('../../quotationStore.types').DraftMetadata>);
            
            // Build new draft metadata
            const draftMeta = {
                estimateId: currentQuotation.metadata.quoteId,
                versionHash: snapshot,
                updatedAt: now,
                userId: null, // Would require session, omit for simplicity or fetch if needed
                tabId: sessionStorage.getItem('tabId') || Math.random().toString(36).substring(7),
                appVersion: '1.0.0',
                content: currentQuotation
            };
            
            // Save to Local Drafts immediately
            set({ 
                localDrafts: { ...activeDrafts, [currentQuotation.metadata.quoteId]: draftMeta },
            });
            
            // Try saving to backend
            await quotationService.saveDraft(currentQuotation);
            
            set({
                autoSaveInFlight: false,
                lastAutoSavedSnapshot: snapshot,
                lastAutoSaveAt: now,
            });
            setSyncStatus('saved');
            
            // Reset status after a few seconds
            setTimeout(() => {
                if (get().syncStatus === 'saved') setSyncStatus('idle');
            }, 3000);
            
        } catch (error) {
            console.error('[AutoSave] Draft sync failed:', error);
            set({ autoSaveInFlight: false });
            setSyncStatus('offline'); // Saved locally, but remote failed
        }
    },

    createNewQuotation: async (clientName, projectName, industryId, projectTypeId, designConsultantId, scopeSelections) => {
        set({ isInitializing: true });
        
        try {
            // Use server-side creation via DB function
            const result = await tenantService.createQuoteFromTemplate({
                projectTypeId,
                clientInfo: {
                    name: clientName,
                    projectName: projectName,
                    bdmId: designConsultantId || null,
                },
                scopeSelections: scopeSelections.map(s => ({
                    type_value: s.category,
                    count: s.count
                })),
            });

            // Fetch the fully initialized quotation from DB
            const newQuotation = await quotationService.getQuotationById(result.quote_id, false);
            
            if (!newQuotation) {
                throw new Error('Failed to retrieve newly created quotation');
            }

            set((state: unknown) => ({
                currentQuotation: newQuotation,
                quotations: [...state.quotations, newQuotation],
                currentUserRole: 'admin',
                isInitializing: false
            }));

            return newQuotation;
        } catch (error) {
            console.error('[Store] Failed to create quotation via template:', error);
            set({ isInitializing: false });
            throw error;
        }
    },

    // ── New Lifecycle Actions (RPC-backed) ─────────────────────────────────────

    createRevision: async (parentId: string): Promise<string | null> => {
        const { currentUserRole } = get();
        if (currentUserRole === 'viewer') {
            throw new Error('Access Denied: Viewers cannot create revisions.');
        }
        set({ isLoading: true, error: null });
        try {
            const newDraftId = await quotationService.createRevision(parentId);
            cacheService.invalidateQuotations();
            set({ isLoading: false });
            return newDraftId;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            set({ isLoading: false, error: msg });
            throw err;
        }
    },

    duplicateAsDraft: async (sourceId: string): Promise<string | null> => {
        const { currentUserRole } = get();
        if (currentUserRole === 'viewer') {
            throw new Error('Access Denied: Viewers cannot duplicate quotations.');
        }
        set({ isLoading: true, error: null });
        try {
            const newDraftId = await quotationService.duplicateAsDraft(sourceId);
            cacheService.invalidateQuotations();
            set({ isLoading: false });
            return newDraftId;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            set({ isLoading: false, error: msg });
            throw err;
        }
    },

    cancelQuotation: async (quoteId: string, reason: string): Promise<void> => {
        const { currentUserRole, currentQuotation } = get();
        if (currentUserRole !== 'admin') {
            throw new Error('Access Denied: Only Admins can cancel quotations.');
        }
        set({ isLoading: true, error: null });
        try {
            await quotationService.cancelQuotation(quoteId, reason);

            // Update local state to reflect cancellation
            if (currentQuotation?.metadata.quoteId === quoteId) {
                set({
                    currentQuotation: {
                        ...currentQuotation,
                        metadata: {
                            ...currentQuotation.metadata,
                            status: 'cancelled' as const,
                            cancellationReason: reason,
                            cancelledAt: new Date().toISOString(),
                        }
                    },
                    isLoading: false,
                });
            } else {
                set({ isLoading: false });
            }

            cacheService.invalidateQuotations();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            set({ isLoading: false, error: msg });
            throw err;
        }
    },

    finalizeQuotation: async (quoteId: string): Promise<void> => {
        const { currentUserRole, currentQuotation } = get();
        if (currentUserRole !== 'admin') {
            throw new Error('Access Denied: Only Admins can finalize quotations.');
        }
        set({ isLoading: true, error: null });
        try {
            const result = await quotationService.finalizeQuotation(quoteId);

            if (currentQuotation?.metadata.quoteId === quoteId) {
                set({
                    currentQuotation: {
                        ...currentQuotation,
                        metadata: {
                            ...currentQuotation.metadata,
                            status: 'finalized' as const,
                            locked: true,
                            isLatestVersion: true,
                            finalizedAt: new Date().toISOString(),
                        }
                    },
                    isLoading: false,
                });
            } else {
                set({ isLoading: false });
            }

            cacheService.invalidateQuotations();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            set({ isLoading: false, error: msg });
            throw err;
        }
    },

    setCurrentQuotation: (quotation) => {
        const snapshot = quotation ? JSON.stringify(quotation) : null;
        set({
            currentQuotation: quotation,
            lastHydratedSnapshot: snapshot,
            lastAutoSavedSnapshot: snapshot,
            autoSaveInFlight: false,
        });
    },

    toggleTemplateStatus: async (quoteId: string, isTemplate: boolean, templateName?: string) => {
        const { currentQuotation, quotations } = get() as any;
        const target = quotations.find((q: Quotation) => q.metadata?.quoteId === quoteId) ||
                       (currentQuotation?.metadata?.quoteId === quoteId ? currentQuotation : null);

        const finalTemplateName = isTemplate ? (templateName || target?.metadata?.templateName || target?.client?.projectName || target?.metadata?.quoteNumber) : undefined;

        // Always persist to Supabase DB via quotationService
        await quotationService.toggleTemplateStatus(quoteId, isTemplate, finalTemplateName);

        set((state: any) => {
            const exists = state.quotations.some((q: Quotation) => q.metadata?.quoteId === quoteId);
            const updatedItem = target
                ? { ...target, metadata: { ...target.metadata, isTemplate, templateName: finalTemplateName } }
                : null;

            let newQuotations = state.quotations;
            if (exists) {
                newQuotations = state.quotations.map((q: Quotation) =>
                    q.metadata?.quoteId === quoteId
                        ? { ...q, metadata: { ...q.metadata, isTemplate, templateName: finalTemplateName } }
                        : q
                );
            } else if (updatedItem) {
                newQuotations = [updatedItem, ...state.quotations];
            }

            const updatedCurrent = state.currentQuotation?.metadata?.quoteId === quoteId
                ? { ...state.currentQuotation, metadata: { ...state.currentQuotation.metadata, isTemplate, templateName: finalTemplateName } }
                : state.currentQuotation;

            return {
                quotations: newQuotations,
                currentQuotation: updatedCurrent,
            };
        });
    },

    cloneQuotation: async (quoteId: string): Promise<Quotation | null> => {
        // Legacy: routes to duplicateAsDraft RPC for data integrity
        const newId = await (get() as any).duplicateAsDraft(quoteId);
        if (!newId) return null;
        // Fetch the new draft so the caller gets a typed Quotation back
        const newQuote = await quotationService.getQuotationById(newId, false);
        if (newQuote) {
            set((state: any) => ({
                currentQuotation: newQuote,
                quotations: [...state.quotations, newQuote],
                currentUserRole: 'admin',
            }));
        }
        return newQuote;
    },
});

export const createQuotationManagementSlice = (createQuotationManagementSliceImpl as unknown as StateCreator<QuotationStore, [], [], QuotationManagementSlice>);
